import { useCallback, useEffect, useState } from "react";

import List from "../../../components/editableGrid/list/list";
import { Project } from "../../../components/createProject/models/project";
import { AppAlert, AppButton, AppIcon, AppMultiActionButton, AppTooltip } from '../../../theme';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { circleInfo, circlePlus, trashCan } from '../../../theme/icons';
import { addEmailDomain, fetchEmailDomains, removeEmailDomain } from "../../../store/apis/projectApi";
import t from "../../../localization/en/translation.json";
import Loader from "../../../components/loader/loader";
import { url } from '../../../components/footer/footerConstants';
import { IUser } from "../../../models/user";
import { domainDeleteValidation } from "../../../utils/manageUserHelpers";

import "./emailDomains.scss";

export const EmailDomains = ({
  record,
  closeDomainModal,
  projectUsersList,
}: {
  record?: Project;
  closeDomainModal: () => void;
  projectUsersList: IUser[];
}) => {
  const [emailDomains, setEmailDomains] = useState<any[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<any[]>([]);
  const [isOnlyIcons, setIsOnlyIcons] = useState<boolean>(false);
  const [rowIndex, setRowIndex] = useState<number>(0);
  const [gridApi, setGridApi] = useState<any>(null);
  const [newRowData, setNewRowData] = useState<any[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertMessageType, setAlertMessageType] = useState<"info" | "error" | "warning" | "success">("info");

  const pinnedObject = { pinned: "left" };

  const domainColumnConfig = [
    {
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      field: "id",
      width: 45,
      ...pinnedObject,
      suppressMovable: true,
      id: "actions",
      lockPosition: true,
      headerClass: "header-checkbox",
      showDisabledCheckboxes: true,
    },
    {
      headerName: "Title",
      field: "name",
      editable: (params) => isRowEditable(params),
      singleClickEdit: true,
      cellEditor: "customTextCellEditor",
      width: 400,
      cellClassRules: {
        'cell-invalid': (params) => {
          if (!params.data.id)
            return Boolean(validateFields("name")(params.value));
          else return false;
        }
      },
      cellRenderer: 'customValidationCellRenderer',
      tooltipComponent: 'customValidationTooltip',
      tooltipValueGetter: (params) => params.value || " ",
      tooltipComponentParams: (params) => {
        return {
          isPinnedRowAvailable: newRowData.some(row => !row.id),
          errorMessage: (!params.data.id) ? validateFields("name")(params.value) : ""
        };
      },
    },
    {
      headerName: "Domain",
      field: "domainExpression",
      editable: (params) => isRowEditable(params),
      flex: 1,
      singleClickEdit: true,
      cellEditor: "customTextCellEditor",
      cellClassRules: {
        'cell-invalid': (params) => {
          if (!params.data.id)
            return Boolean(validateFields("domainExpression")(params.value));
          else return false;
        }
      },
      cellRenderer: 'customValidationCellRenderer',
      tooltipComponent: 'customValidationTooltip',
      tooltipValueGetter: (params) => params.value || " ",
      tooltipComponentParams: (params) => {
        return {
          isPinnedRowAvailable: newRowData.some(row => !row.id),
          errorMessage: !params.data.id ? validateFields("domainExpression")(params.value) : ""
        };
      },
    }
  ];

  const validateFields = (field) => {
    return ((value) => {
      let message = "";
      switch (field) {
        case "name":
          if (!value || value?.trim() === "")
            message = t.required_field;
          else if (value.length > 50)
            message = t.title_field_max_limit_validation_message;
          else if (!(/^[a-zA-Z0-9](.*[a-zA-Z0-9])/).test(value))
            message = t.field_invalid_message;
          break;
        case "domainExpression":
          if (value && value?.trim() === "")
            message = t.required_field;
          else
            message = (/^((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,6}$/).test(value) ? "" : t.invalid_domain;
          break;
        default:
          message = ""
      }
      return message;
    })
  }

  const onGridReady = ({ api }) => {
    setGridApi(api);
  };

  const isRowEditable = (params) => {
    return params.data.id ? false : true;
  }

  const clearAlert = () => {
    setAlertMessage("");
    setAlertMessageType("info");
  }

  const getEmailDomains = useCallback(async () => {
    try {
      setShowLoader(true);
      if (record && record.id) {
        const response = await fetchEmailDomains(record.id);
        setEmailDomains(response?.map(item => ({ ...item, key: item.id })) || []);
        setShowLoader(false);
      }
    } catch (error) {
      setShowLoader(false);
    }
  }, [record]);

  const addNewRow = useCallback(() => {
    if (!gridApi)
      return;
    setEmailDomains((prev) => [{ id: null, name: "", domainExpression: "", key: Date.now() }, ...prev]);
    setRowIndex(rowIndex + 1);
    setTimeout(() => { gridApi.startEditingCell({ rowIndex: 0, colKey: "name" }); }, 0);
  }, [gridApi, rowIndex]);

  const hasErrorInGridCells = () => {
    const gridElement = document.querySelector('.domains-grid-container .app-table');
    const errorCells = gridElement?.querySelectorAll('.ag-cell.cell-invalid') || [];
    return errorCells.length > 0
  }

  const saveDomain = async (isSaveAndClose: boolean) => {
    if (!gridApi)
      return;

    if (record && record.id) {
      const payload = { emailDomains: newRowData.filter(row => !row.id).map(row => ({ name: row.name?.trim(), domainExpression: row.domainExpression?.trim() })) };
      try {
        setShowLoader(true);
        await addEmailDomain(record.id, payload)
        gridApi.setGridOption("pinnedTopRowData", []);
        setNewRowData([]);
        setAlertMessage(t.domain_save_success);
        setAlertMessageType("success");
        getEmailDomains();
        setTimeout(() => {
          if (isSaveAndClose)
            closeDomainModal();
          setShowLoader(false);
        }, 1000);
      } catch (error: any) {
        setShowLoader(false);
        setAlertMessage(error?.response?.data[0]?.errorMessage);
        setAlertMessageType("warning");
      }
    }
  }

  const removeDomain = async () => {
    console.log(emailDomains)
    if (!gridApi)
      return;

    const selectedRows = gridApi.getSelectedRows();
    const { count, domainName } = selectedRows?.length && domainDeleteValidation(selectedRows, projectUsersList);
    if (count && domainName) {
      setAlertMessage(t.domain_user_exists.replace("{|domainName|}", domainName).replace("{|usersNo|}", count.toString()));
      setAlertMessageType("warning");
      return;
    }
    if (record && record.id) {
      const payload = selectedRows.filter(row => row.id).map(row => row.id);
      if (payload.length) {
        try {
          setShowLoader(true);
          await removeEmailDomain(record.id, payload);
          selectedRows.forEach(row => {
            setEmailDomains((prev) => prev.filter(domain => domain.key !== row.key));
            setNewRowData((prev) => prev.filter(domain => domain.key !== row.key));
          });
          setAlertMessage(t.domain_delete_success);
          setAlertMessageType("success");
          getEmailDomains();
          setShowLoader(false);
        } catch (error: any) {
          setShowLoader(false);
          setAlertMessage(error?.response?.data[0]?.errorMessage);
          setAlertMessageType("warning");
        }
      }
    }
  }

  const onRowDataChanged = useCallback((data) => {
    const updatedNewRowData = newRowData
    updatedNewRowData[rowIndex] = data.data;
    setNewRowData(updatedNewRowData.map(row => row));
  }, [newRowData, rowIndex]);

  const onSelectionChanged = useCallback(() => {
    if (gridApi) {
      setSelectedDomains(gridApi.getSelectedRows());
    }
  }, [gridApi]);

  useEffect(() => {
    getEmailDomains();
  }, [getEmailDomains]);


  return (
    <>
      <div className="email-domains-container">
        <Loader isLoading={showLoader} isFullSize={false} />
        {alertMessage && alertMessageType &&
          <AppAlert severity={alertMessageType} className="email-domains-alerts" onClose={() => clearAlert()}>
            {alertMessage}
          </AppAlert>
        }
        <div className="grid-header-toolbar">
          <div className="toolbar-left">
            <div className={`toolbar-group ${isOnlyIcons && 'expanded'}`}>
              <div
                className={`toolbar-item`}
                onClick={addNewRow}
                title={t.add_new}
              >
                {isOnlyIcons ? (
                  <AppTooltip
                    placement={'top'}
                    className="roles-selector"
                    title={t.add_new}
                  >
                    <AppIcon icon={circlePlus} />
                  </AppTooltip>
                ) : (
                  <>
                    <AppIcon icon={circlePlus} />
                    <span>{t.add_new}</span>
                  </>
                )}
              </div>
              <div className={`toolbar-item ${(!selectedDomains.length) && 'disabled'}`} onClick={removeDomain} title={t.delete}>
                {isOnlyIcons ? (
                  <AppTooltip
                    placement={'top'}
                    className="roles-selector"
                    title={t.delete}
                  >
                    <AppIcon icon={trashCan} />
                  </AppTooltip>
                ) : (
                  <>
                    <AppIcon icon={trashCan} />
                    <span>{t.delete}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="toolbar-right">
            <div
              className={`expand-icon ${isOnlyIcons ? 'expanded' : ''}`}
              onClick={() => setIsOnlyIcons(!isOnlyIcons)}>
              <ExpandLessIcon />
            </div>
          </div>
        </div>

        <div className="domains-grid-container">
          <List
            containerClassName="motif-table"
            setDomLayout="autoHeight"
            rowHeight={40}
            firstColumnBorderRight={true}
            withStickyScroll={false}
            columnConfig={domainColumnConfig}
            rowData={emailDomains}
            paginate={false}
            onGridReady={onGridReady}
            onCellValueChanged={onRowDataChanged}
            rowSelection="multiple"
            onSelectionChanged={onSelectionChanged}
            gridOptions={{
              suppressRowClickSelection: true,
              singleClickEdit: true,
              headerHeight: 44,
              isRowSelectable: (params) => {
                return params.data && params.data.id !== undefined;
              },
            }}
            stopEditingWhenCellsLoseFocus={true}
          />
        </div>
        <div className="emg-label-container"><AppIcon icon={circleInfo} style={{ marginRight: 10 }} />
          <span dangerouslySetInnerHTML={{ __html: t.email_domain_emg_reference.replaceAll("{|EMG|}", `<a href="${url.EMG}" target="_blank" class="">EMG</a>`) }}></span>
        </div>
        <div className="grid-footer">
          <div>
            <AppButton color="secondary" className="cancel-btn" onClick={closeDomainModal}> Cancel </AppButton>
            <AppMultiActionButton actions={[
              { label: t.save_and_close, onClick: () => saveDomain(true) },
              { label: t.save, onClick: () => saveDomain(false) }
            ]} disabled={!newRowData.length || hasErrorInGridCells()} containerId="email-domains-modal-action-btn" />
          </div>
        </div>
      </div>
    </>
  );
}
